// components/EditProperty.jsx
import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { updateProperty } from "../redux/slices/propertySlice";

const AVAILABLE_FOR_OPTIONS = [
  "family", "bachelor_boy", "bachelor_girl", "only_boys", "only_girls", "all",
];
const FURNISHING_OPTIONS = ["unfurnished", "semi_furnished", "fully_furnished"];

const formatLabel = (str) =>
  str ? str.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : str;

const DEFAULT_FORM = {
  name: "",
  description: "",
  availableFor: "all",
  furnishing: "unfurnished",
  rentAmount: "",
  securityDeposit: "",
  rentAgreement: "",
  area: "",
  propertyAge: "",
  isAvailable: true,
  isFeatured: false,
  amenities: "",        // stored as comma-separated string in form
  location: {
    address: "",
    city: "",
    state: "",
    country: "",
    zipCode: "",
    lat: "",
    lng: "",
  },
};

export default function EditProperty({ property, onClose }) {
  const dispatch = useDispatch();
  const [form, setForm] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [validated, setValidated] = useState(false);

  useEffect(() => {
    if (!property) return;
    setForm({
      name: property.name ?? "",
      description: property.description ?? "",
      availableFor: property.availableFor ?? "all",
      furnishing: property.furnishing ?? "unfurnished",
      rentAmount: property.rentAmount ?? "",
      securityDeposit: property.securityDeposit ?? "",
      rentAgreement: property.rentAgreement ?? "",
      area: property.area ?? "",
      propertyAge: property.propertyAge ?? "",
      isAvailable: property.isAvailable ?? true,
      isFeatured: property.isFeatured ?? false,
      amenities: Array.isArray(property.amenities)
        ? property.amenities.join(", ")
        : property.amenities ?? "",
      location: {
        address: property.location?.address ?? "",
        city: property.location?.city ?? "",
        state: property.location?.state ?? "",
        country: property.location?.country ?? "",
        zipCode: property.location?.zipCode ?? "",
        lat: property.location?.lat ?? "",
        lng: property.location?.lng ?? "",
      },
    });
  }, [property]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const set = (field, value) =>
    setForm((f) => ({ ...f, [field]: value }));

  const setLoc = (field, value) =>
    setForm((f) => ({ ...f, location: { ...f.location, [field]: value } }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidated(true);

    if (!form.name.trim() || !form.rentAmount) return;

    setSaving(true);
    setError(null);

    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      availableFor: form.availableFor,
      furnishing: form.furnishing,
      rentAmount: Number(form.rentAmount),
      securityDeposit: form.securityDeposit !== "" ? Number(form.securityDeposit) : undefined,
      rentAgreement: form.rentAgreement !== "" ? Number(form.rentAgreement) : undefined,
      area: form.area !== "" ? Number(form.area) : undefined,
      propertyAge: form.propertyAge !== "" ? Number(form.propertyAge) : undefined,
      isAvailable: form.isAvailable,
      isFeatured: form.isFeatured,
      amenities: form.amenities
        ? form.amenities.split(",").map((a) => a.trim()).filter(Boolean)
        : [],
      location: {
        address: form.location.address,
        city: form.location.city,
        state: form.location.state,
        country: form.location.country,
        zipCode: form.location.zipCode,
        lat: form.location.lat !== "" ? Number(form.location.lat) : undefined,
        lng: form.location.lng !== "" ? Number(form.location.lng) : undefined,
      },
    };

    const result = await dispatch(updateProperty({ id: property._id, data: payload }));
    setSaving(false);

    if (updateProperty.fulfilled.match(result)) {
      setSuccess(true);
      setTimeout(onClose, 1000);
    } else {
      setError(result.payload || "Update failed. Please try again.");
    }
  };

  return (
    <div
      className="modal show d-block"
      style={{ background: "rgba(0,0,0,0.45)", overflowY: "auto" }}
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-lg modal-dialog-scrollable my-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content">
          {/* Header */}
          <div className="modal-header">
            <h5 className="modal-title">Edit Property</h5>
            <button className="btn-close" onClick={onClose} />
          </div>

          {/* Body */}
          <div className="modal-body">
            {success && (
              <div className="alert alert-success py-2">
                ✅ Property updated successfully!
              </div>
            )}
            {error && (
              <div className="alert alert-danger py-2">{error}</div>
            )}

            <form noValidate onSubmit={handleSubmit} id="edit-property-form">

              {/* ── Section: Basic Info ── */}
              <h6 className="text-muted fw-semibold border-bottom pb-1 mb-3">Basic Information</h6>

              <div className="mb-3">
                <label className="form-label">Property Name <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className={`form-control ${validated && !form.name.trim() ? "is-invalid" : ""}`}
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  placeholder="e.g. 2BHK Flat in Sector 22"
                />
                <div className="invalid-feedback">Name is required.</div>
              </div>

              <div className="mb-3">
                <label className="form-label">Description</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={form.description}
                  onChange={(e) => set("description", e.target.value)}
                  placeholder="Describe the property..."
                />
              </div>

              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <label className="form-label">Available For</label>
                  <select
                    className="form-select"
                    value={form.availableFor}
                    onChange={(e) => set("availableFor", e.target.value)}
                  >
                    {AVAILABLE_FOR_OPTIONS.map((o) => (
                      <option key={o} value={o}>{formatLabel(o)}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Furnishing</label>
                  <select
                    className="form-select"
                    value={form.furnishing}
                    onChange={(e) => set("furnishing", e.target.value)}
                  >
                    {FURNISHING_OPTIONS.map((o) => (
                      <option key={o} value={o}>{formatLabel(o)}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Amenities <small className="text-muted">(comma-separated)</small></label>
                <input
                  type="text"
                  className="form-control"
                  value={form.amenities}
                  onChange={(e) => set("amenities", e.target.value)}
                  placeholder="WiFi, Parking, Gym, Lift..."
                />
              </div>

              {/* ── Section: Pricing ── */}
              <h6 className="text-muted fw-semibold border-bottom pb-1 mb-3 mt-4">Pricing & Details</h6>

              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <label className="form-label">Rent Amount (₹/mo) <span className="text-danger">*</span></label>
                  <input
                    type="number"
                    className={`form-control ${validated && !form.rentAmount ? "is-invalid" : ""}`}
                    value={form.rentAmount}
                    onChange={(e) => set("rentAmount", e.target.value)}
                    placeholder="e.g. 15000"
                    min="0"
                  />
                  <div className="invalid-feedback">Rent amount is required.</div>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Security Deposit (₹)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={form.securityDeposit}
                    onChange={(e) => set("securityDeposit", e.target.value)}
                    placeholder="e.g. 30000"
                    min="0"
                  />
                </div>
              </div>

              <div className="row g-3 mb-3">
                <div className="col-md-4">
                  <label className="form-label">Rent Agreement (months)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={form.rentAgreement}
                    onChange={(e) => set("rentAgreement", e.target.value)}
                    placeholder="e.g. 11"
                    min="0"
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Area (sqft)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={form.area}
                    onChange={(e) => set("area", e.target.value)}
                    placeholder="e.g. 850"
                    min="0"
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Property Age (years)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={form.propertyAge}
                    onChange={(e) => set("propertyAge", e.target.value)}
                    placeholder="e.g. 3"
                    min="0"
                  />
                </div>
              </div>

              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="isAvailable"
                      checked={form.isAvailable}
                      onChange={(e) => set("isAvailable", e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="isAvailable">
                      Available for Rent
                    </label>
                  </div>
                </div>
              
              </div>

              {/* ── Section: Location ── */}
              <h6 className="text-muted fw-semibold border-bottom pb-1 mb-3 mt-4">Location</h6>

              <div className="mb-3">
                <label className="form-label">Address</label>
                <input
                  type="text"
                  className="form-control"
                  value={form.location.address}
                  onChange={(e) => setLoc("address", e.target.value)}
                  placeholder="Street / Sector / Society"
                />
              </div>

              <div className="row g-3 mb-3">
                <div className="col-md-4">
                  <label className="form-label">City</label>
                  <input
                    type="text"
                    className="form-control"
                    value={form.location.city}
                    onChange={(e) => setLoc("city", e.target.value)}
                    placeholder="e.g. Chandigarh"
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">State</label>
                  <input
                    type="text"
                    className="form-control"
                    value={form.location.state}
                    onChange={(e) => setLoc("state", e.target.value)}
                    placeholder="e.g. Punjab"
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Country</label>
                  <input
                    type="text"
                    className="form-control"
                    value={form.location.country}
                    onChange={(e) => setLoc("country", e.target.value)}
                    placeholder="e.g. India"
                  />
                </div>
              </div>

              <div className="row g-3 mb-1">
                <div className="col-md-4">
                  <label className="form-label">Zip Code</label>
                  <input
                    type="text"
                    className="form-control"
                    value={form.location.zipCode}
                    onChange={(e) => setLoc("zipCode", e.target.value)}
                    placeholder="e.g. 160001"
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Latitude</label>
                  <input
                    type="number"
                    className="form-control"
                    value={form.location.lat}
                    onChange={(e) => setLoc("lat", e.target.value)}
                    placeholder="e.g. 30.7333"
                    step="any"
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Longitude</label>
                  <input
                    type="number"
                    className="form-control"
                    value={form.location.lng}
                    onChange={(e) => setLoc("lng", e.target.value)}
                    placeholder="e.g. 76.7794"
                    step="any"
                  />
                </div>
              </div>

            </form>
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button
              type="submit"
              form="edit-property-form"
              className="btn btn-primary"
              disabled={saving || success}
              onClick={handleSubmit}
            >
              {saving ? (
                <><span className="spinner-border spinner-border-sm me-1" /> Saving…</>
              ) : success ? (
                "✓ Saved"
              ) : (
                "Save Changes"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
